/**
 * HighlightRenderer — walks the DOM inside containerRef and wraps text
 * matching each highlight note's anchor_data.selected_text in a <mark>
 * element so previously highlighted passages are visually restored.
 *
 * Uses a non-destructive TreeWalker + Range approach: it only inserts
 * <mark> nodes; original DOM structure is otherwise preserved.
 */
import { useEffect } from 'react';

const HIGHLIGHT_STYLE = [
  'background: rgba(251,191,36,0.3)',
  'border-radius: 2px',
  'cursor: default',
].join(';');

function applyHighlight(container, text) {
  if (!container || !text || !text.trim()) return;

  // Walk all text nodes inside the container
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        // Skip nodes inside existing highlight marks to avoid double-wrapping
        if (node.parentElement?.closest('mark[data-teachiq-highlight]')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  const needle = text.trim();
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) {
    nodes.push(node);
  }

  // Build a concatenated text map to find cross-node matches
  let offset = 0;
  const spans = nodes.map((n) => {
    const start = offset;
    offset += n.nodeValue.length;
    return { node: n, start, end: offset };
  });

  const fullText = nodes.map((n) => n.nodeValue).join('');
  let searchFrom = 0;
  const matchPositions = [];

  while (searchFrom < fullText.length) {
    const idx = fullText.indexOf(needle, searchFrom);
    if (idx === -1) break;
    matchPositions.push({ start: idx, end: idx + needle.length });
    searchFrom = idx + needle.length;
  }

  if (matchPositions.length === 0) return;

  // Apply marks in reverse to avoid offset shifts
  for (let m = matchPositions.length - 1; m >= 0; m--) {
    const { start: mStart, end: mEnd } = matchPositions[m];

    // Find the text nodes that cover this match range
    const involved = spans.filter((s) => s.end > mStart && s.start < mEnd);
    if (involved.length === 0) continue;

    if (involved.length === 1) {
      // Contained within a single text node — simple case
      const { node: tn, start: tnStart } = involved[0];
      const localStart = mStart - tnStart;
      const localEnd = mEnd - tnStart;
      try {
        const range = document.createRange();
        range.setStart(tn, localStart);
        range.setEnd(tn, localEnd);
        const mark = document.createElement('mark');
        mark.setAttribute('data-teachiq-highlight', '');
        mark.setAttribute('style', HIGHLIGHT_STYLE);
        range.surroundContents(mark);
      } catch {
        // Range crosses element boundary — skip gracefully
      }
    } else {
      // Spans multiple text nodes — wrap each fragment individually
      for (const { node: tn, start: tnStart, end: tnEnd } of involved) {
        const localStart = Math.max(0, mStart - tnStart);
        const localEnd = Math.min(tnEnd - tnStart, mEnd - tnStart);
        if (localStart >= localEnd) continue;
        try {
          const range = document.createRange();
          range.setStart(tn, localStart);
          range.setEnd(tn, localEnd);
          const mark = document.createElement('mark');
          mark.setAttribute('data-teachiq-highlight', '');
          mark.setAttribute('style', HIGHLIGHT_STYLE);
          range.surroundContents(mark);
        } catch {
          // Skip if surroundContents fails (partial element boundary)
        }
      }
    }
  }
}

function removeAllHighlights(container) {
  if (!container) return;
  const marks = container.querySelectorAll('mark[data-teachiq-highlight]');
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
}

export default function HighlightRenderer({ containerRef, highlights }) {
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    // Clear any previously applied marks before re-applying
    removeAllHighlights(container);

    if (!highlights || highlights.length === 0) return;

    for (const h of highlights) {
      const text = h.anchor_data?.selected_text || h.text;
      if (text) applyHighlight(container, text);
    }

    // Cleanup: remove marks when highlights change or component unmounts
    return () => removeAllHighlights(container);
  }, [containerRef, highlights]);

  return null;
}
